import { useEffect, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { Button } from '@/design-system';

const SHOW_AFTER_PX = 240;

function findScrollParent(el: HTMLElement | null): HTMLElement | Window {
  let parent = el?.parentElement ?? null;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
}

type BackToTopProps = {
  headerId: string;
};

export function BackToTop({ headerId }: BackToTopProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const scrollParent = findScrollParent(sentinel);

    const onScroll = () => {
      const top =
        scrollParent === window
          ? window.scrollY
          : (scrollParent as HTMLElement).scrollTop;
      setVisible(top >= SHOW_AFTER_PX);
    };

    onScroll();
    scrollParent.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollParent.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToHeader = () => {
    const header = document.getElementById(headerId);
    if (!header) return;
    header.scrollIntoView({ behavior: 'smooth', block: 'start' });
    header.focus({ preventScroll: true });
  };

  return (
    <>
      <div ref={sentinelRef} className="hidden" aria-hidden="true" />
      {visible ? (
        <Button
          type="button"
          variant="ghost"
          className="fixed bottom-6 right-24 z-30 gap-1.5"
          onClick={scrollToHeader}
          aria-label="Back to top"
          leftIcon={<ArrowUp className="h-4 w-4" aria-hidden="true" />}
        >
          Top
        </Button>
      ) : null}
    </>
  );
}
